
import { FormsModule } from '@angular/forms';
import { Component, OnInit, SimpleChanges, ElementRef, ChangeDetectorRef, ChangeDetectionStrategy, inject, input, output, viewChild } from '@angular/core';
import { throwError } from 'rxjs';

@Component({
    imports: [FormsModule],
    selector: 'qty-selector',
    template: `
    <div
      class="w-full h-full relative overflow-hidden rounded flex-1 select-none bg-vert-3 fg-vert-1 select-none"
      >
      <section
        [class.animate-swipe-from-right]="this.quantity > 0"
        [class.animate-swipe-left]="this.quantity === 0"
        class="absolute w-full h-full flex items-center justify-center gap-1 bg-vert-3"
        >
        <button
          (click)="this.incrementQuantity(-1)"
          class="cursor-pointer hover:bg-vert-1 hover:fg-vert-3 transition-all fadeIn opacity-0 flex items-center justify-center flex-1 h-full fg-vert-1 fadeIn opacity-0"
          >
          <span class="material-icons">remove</span>
        </button>
        <span
          class="fadeIn opacity-0 flex items-center justify-center flex-1 h-full fg-vert-1 text-lg font-extrabold"
          >
          @if (!isEditing) {
            <span
              [class.animate-[bounce_300ms_ease_infinite]]="animateDisplay"
              (click)="startEditing()"
              class="cursor-pointer"
              >
              {{ this.quantity }}
            </span>
          }
          @if (isEditing) {
            <input
              #qtyInput
              type="number"
              [(ngModel)]="editValue"
              [min]="min()"
              [max]="max()"
              (blur)="finishEditing()"
              (keyup.enter)="finishEditing()"
              class="w-16 text-center bg-transparent fg-vert-1 border border-vert-1 rounded"
              />
          }
        </span>
        <button
          (click)="this.incrementQuantity(1)"
          class="cursor-pointer hover:bg-vert-1 hover:fg-vert-3 transition-all fadeIn opacity-0 flex items-center justify-center flex-1 h-full fg-vert-1"
          >
          <span class="material-icons">add</span>
        </button>
      </section>
    </div>
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./QuantitySelector.component.scss']
})
export class QuantitySelectorComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);

  animate = false;
  animateDisplay = false;
  quantity: number = 1;
  isEditing = false;
  editValue: number = 1;

  readonly max = input<number>(100);

  readonly min = input<number>(1);

  readonly value = input<number>(1);

  readonly quantityChange = output<number>();
  
  readonly qtyInput = viewChild.required<ElementRef>('qtyInput');

  ngOnInit() {
    this.quantityChange.emit(this.quantity);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['quantity']) {
      this.triggerAnimation();
    }

    if (changes['value']) {
      this.quantity = this.value();
      this.editValue = this.value();
    }
  }

  triggerAnimation() {
    // Utilisation d'une variable distincte pour l'animation
    this.animateDisplay = true;

    setTimeout(() => {
      this.animateDisplay = false;
      this.cdr.detectChanges();
    }, 300);
  }

  launchError(errorMessage: string) {
    
    throwError(() => new Error(errorMessage));
  }

  incrementQuantity(quantity: number) {
    let n = this.quantity;
    n += quantity;

    if (n <= this.min()) {
      n = this.min();
    }

    if (n >= this.max()) {
      n = this.max();
    }

    if (this.quantity != n) {
      this.triggerAnimation();
    }

    this.quantity = n;
    this.editValue = n;
    this.quantityChange.emit(this.quantity);
  }

  startEditing() {
    this.isEditing = true;
    this.editValue = this.quantity;
    
    // Focus l'input après le rendu DOM
    setTimeout(() => {
      const qtyInput = this.qtyInput();
      if (qtyInput) {
        qtyInput.nativeElement.focus();
        qtyInput.nativeElement.select();
      }
    }, 0);
  }

  finishEditing() {
    this.isEditing = false;
    
    // Parse la valeur entrée
    let newValue: number;
    
    if (typeof this.editValue === 'string') {
      newValue = parseInt(this.editValue);
    } else {
      newValue = this.editValue;
    }
    
    // Vérifier si c'est un nombre valide
    if (isNaN(newValue)) {
      newValue = this.quantity;
    }
    
    // Respecter min et max
    if (newValue < this.min()) {
      newValue = this.min();
    }
    
    if (newValue > this.max()) {
      newValue = this.max();
    }
    
    // Mettre à jour uniquement si la valeur a changé
    if (this.quantity !== newValue) {
      this.quantity = newValue;
      this.triggerAnimation();
      this.quantityChange.emit(this.quantity);
    }
  }
}